import React, { useState, useMemo } from 'react';

/* ==========================================
   HELPER: TITLE CASE FORMATTER
   ========================================== */
const toTitleCase = (str) => {
  if (!str || typeof str !== 'string') return str || '';
  return str
    .trim()
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (!word) return '';
      if (word.includes('-')) {
        return word.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

export function InteractivePlanBuilder({
  initialTitle = "",
  initialNotes = "",
  initialDurationWeeks = 12,
  initialIncludeDeload = true,
  initialAssignmentMode = 'ALL_TRAINEES',
  initialAssignedTrainee = null,
  initialDays = [],
  availableRoutines = [],
  activeTrainees = [],
  isGlobalOnly = false,
  lang = 'pl',
  onSave,
  onCancel
}) {
  // Plan Metadata State
  const [title, setTitle] = useState(initialTitle);
  const [notes, setNotes] = useState(initialNotes);
  const [durationWeeks, setDurationWeeks] = useState(initialDurationWeeks || 12);
  const [includeDeload, setIncludeDeload] = useState(initialIncludeDeload ?? true);
  const [isSaving, setIsSaving] = useState(false);

  // Single Unified Assignment Selection State
  const [assignmentSelection, setAssignmentSelection] = useState(() => {
    if (isGlobalOnly) return 'TEMPLATE_ONLY';
    if (initialAssignedTrainee?.id) return `TRAINEE_${initialAssignedTrainee.id}`;
    return initialAssignmentMode || 'ALL_TRAINEES';
  });

  // Native HTML5 Drag and Drop State
  const [draggedIdx, setDraggedIdx] = useState(null);

  // Days Schedule State (Single Intuitive List)
  const [days, setDays] = useState(() => {
    if (initialDays && initialDays.length > 0) {
      return initialDays.map((d, idx) => ({
        id: `day_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 4)}`,
        dayNumber: d.dayNumber || idx + 1,
        dayLabel: d.dayLabel || (lang === 'pl' ? `Dzień ${idx + 1}` : `Day ${idx + 1}`),
        dayType: d.dayType || 'workout', // 'workout' | 'rest'
        routineId: d.routineId || '',
        routineTitle: d.routineTitle || '',
        notes: d.notes || '',
        showNotes: !!d.notes
      }));
    }

    // Default 7-day Schedule
    const defaultDays = [
      { dayLabel: lang === 'pl' ? 'Dzień 1 - Push (Klatka/Barki/Triceps)' : 'Day 1 - Push (Chest/Shoulders/Triceps)', dayType: 'workout' },
      { dayLabel: lang === 'pl' ? 'Dzień 2 - Pull (Plecy/Biceps)' : 'Day 2 - Pull (Back/Biceps)', dayType: 'workout' },
      { dayLabel: lang === 'pl' ? 'Dzień 3 - Odpoczynek & Regeneracja' : 'Day 3 - Rest & Recovery', dayType: 'rest' },
      { dayLabel: lang === 'pl' ? 'Dzień 4 - Legs (Uda/Pośladki/Łydki)' : 'Day 4 - Legs (Quads/Hamstrings/Calves)', dayType: 'workout' },
      { dayLabel: lang === 'pl' ? 'Dzień 5 - Upper (Góra Ciała Accent)' : 'Day 5 - Upper (Upper Body Focus)', dayType: 'workout' },
      { dayLabel: lang === 'pl' ? 'Dzień 6 - Aktywny Odpoczynek' : 'Day 6 - Active Rest', dayType: 'rest' },
      { dayLabel: lang === 'pl' ? 'Dzień 7 - Odpoczynek' : 'Day 7 - Rest', dayType: 'rest' }
    ];

    return defaultDays.map((d, idx) => ({
      id: `day_def_${idx}_${Math.random().toString(36).substr(2, 4)}`,
      dayNumber: idx + 1,
      dayLabel: d.dayLabel,
      dayType: d.dayType,
      routineId: '',
      routineTitle: '',
      notes: '',
      showNotes: false
    }));
  });

  // Routine Preview Modal State
  const [previewRoutine, setPreviewRoutine] = useState(null);

  // Filtered Routines List
  const routinesList = useMemo(() => {
    return (availableRoutines || []).filter(r => r && (r.title || r.name) && String(r.title || r.name).trim() !== "");
  }, [availableRoutines]);

  // Telemetry Calculations
  const telemetry = useMemo(() => {
    let workoutDaysCount = 0;
    let restDaysCount = 0;
    let totalWeeklySets = 0;
    let totalWeeklyDurationMin = 0;

    days.forEach(d => {
      if (d.dayType === 'rest') {
        restDaysCount += 1;
        return;
      }

      workoutDaysCount += 1;
      if (d.routineId) {
        const targetId = String(d.routineId).trim();
        const matchedRt = routinesList.find(r => 
          String(r.id || '').trim() === targetId || 
          String(r.docId || '').trim() === targetId ||
          String(r.routineId || '').trim() === targetId
        );

        if (matchedRt) {
          const exArray = matchedRt.exercises || matchedRt.exerciseList || matchedRt.routineExercises || [];
          let setsInRoutine = 0;

          if (Array.isArray(exArray) && exArray.length > 0) {
            exArray.forEach(ex => {
              if (Array.isArray(ex.setsList) && ex.setsList.length > 0) {
                setsInRoutine += ex.setsList.length;
              } else if (Array.isArray(ex.sets) && ex.sets.length > 0) {
                setsInRoutine += ex.sets.length;
              } else {
                const count = parseInt(ex.setsCount || ex.sets) || 3;
                setsInRoutine += count;
              }
            });
          } else {
            setsInRoutine = parseInt(matchedRt.totalSets || matchedRt.setsCount) || 12;
          }

          const routineMinutes = matchedRt.estimatedDurationMinutes || matchedRt.durationMinutes || Math.max(15, Math.round(setsInRoutine * 2.5));

          totalWeeklySets += setsInRoutine;
          totalWeeklyDurationMin += routineMinutes;
        }
      }
    });

    const totalBlockVolume = totalWeeklySets * (durationWeeks || 1);

    return {
      workoutDaysCount,
      restDaysCount,
      totalWeeklySets,
      totalWeeklyDurationMin: Math.round(totalWeeklyDurationMin),
      totalBlockVolume
    };
  }, [days, routinesList, durationWeeks]);

  // Drag and Drop Event Handlers
  const handleDragStart = (e, idx) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetIdx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === targetIdx) return;

    setDays(prev => {
      const updated = [...prev];
      const [draggedItem] = updated.splice(draggedIdx, 1);
      updated.splice(targetIdx, 0, draggedItem);
      return updated.map((d, i) => ({ ...d, dayNumber: i + 1 }));
    });
    setDraggedIdx(null);
  };

  // Day Handlers
  const handleToggleDayType = (idx) => {
    setDays(prev => {
      const updated = [...prev];
      const nextType = updated[idx].dayType === 'workout' ? 'rest' : 'workout';
      updated[idx] = {
        ...updated[idx],
        dayType: nextType,
        routineId: nextType === 'rest' ? '' : updated[idx].routineId,
        routineTitle: nextType === 'rest' ? '' : updated[idx].routineTitle
      };
      return updated;
    });
  };

  const handleUpdateDayField = (idx, field, value) => {
    setDays(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'routineId') {
        const targetId = String(value).trim();
        const rt = routinesList.find(r => 
          String(r.id || '').trim() === targetId || 
          String(r.docId || '').trim() === targetId ||
          String(r.routineId || '').trim() === targetId
        );
        updated[idx].routineTitle = rt ? (rt.title || rt.name) : '';
      }
      return updated;
    });
  };

  const handleDuplicateDay = (idx) => {
    setDays(prev => {
      const target = prev[idx];
      const dup = {
        ...target,
        id: `day_dup_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
        dayLabel: `${target.dayLabel} (${lang === 'pl' ? 'Kopia' : 'Copy'})`
      };
      const updated = [...prev];
      updated.splice(idx + 1, 0, dup);
      return updated.map((d, i) => ({ ...d, dayNumber: i + 1 }));
    });
  };

  const handleRemoveDay = (idx) => {
    if (days.length <= 1) return;
    setDays(prev => prev.filter((_, i) => i !== idx).map((d, i) => ({ ...d, dayNumber: i + 1 })));
  };

  const handleAddDay = (type = 'workout') => {
    setDays(prev => [
      ...prev,
      {
        id: `day_new_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
        dayNumber: prev.length + 1,
        dayLabel: type === 'rest' 
          ? (lang === 'pl' ? `Odpoczynek (Dzień ${prev.length + 1})` : `Rest (Day ${prev.length + 1})`)
          : (lang === 'pl' ? `Trening (Dzień ${prev.length + 1})` : `Workout (Day ${prev.length + 1})`),
        dayType: type,
        routineId: '',
        routineTitle: '',
        notes: '',
        showNotes: false
      }
    ]);
  };

  // Preset Splits Loaders (Fully Localized)
  const handleApplyPresetSplit = (presetKey) => {
    if (!window.confirm(lang === 'pl' ? "Czy na pewno chcesz wgrać gotowy szablon podziału dni? Nadpisze to obecny harmonogram." : "Are you sure you want to load this preset split? This will overwrite the current schedule.")) {
      return;
    }

    let newDaysList = [];

    if (presetKey === 'PPL_6_DAY') {
      newDaysList = [
        { dayLabel: lang === 'pl' ? 'Dzień 1 - Push (Klatka/Barki/Triceps)' : 'Day 1 - Push (Chest/Shoulders/Triceps)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 2 - Pull (Plecy/Tył Barków/Biceps)' : 'Day 2 - Pull (Back/Rear Delts/Biceps)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 3 - Legs (Uda/Pośladki/Łydki)' : 'Day 3 - Legs (Quads/Hamstrings/Calves)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 4 - Push (Góra Ciała Accent)' : 'Day 4 - Push (Upper Focus)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 5 - Pull (Szerokość i Grubość)' : 'Day 5 - Pull (Lats & Thickness)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 6 - Legs (Hipertrofia)' : 'Day 6 - Legs (Hypertrophy)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 7 - Aktywny Odpoczynek' : 'Day 7 - Active Rest', dayType: 'rest' }
      ];
    } else if (presetKey === 'UPPER_LOWER_4_DAY') {
      newDaysList = [
        { dayLabel: lang === 'pl' ? 'Dzień 1 - Góra A (Siła & Masa)' : 'Day 1 - Upper A (Strength & Size)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 2 - Dół A (Quad Focus)' : 'Day 2 - Lower A (Quad Focus)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 3 - Odpoczynek i Regeneracja' : 'Day 3 - Rest & Recovery', dayType: 'rest' },
        { dayLabel: lang === 'pl' ? 'Dzień 4 - Góra B (Hipertrofia)' : 'Day 4 - Upper B (Hypertrophy)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 5 - Dół B (Posterior Chain)' : 'Day 5 - Lower B (Posterior Chain)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 6 - Odpoczynek' : 'Day 6 - Rest', dayType: 'rest' },
        { dayLabel: lang === 'pl' ? 'Dzień 7 - Odpoczynek' : 'Day 7 - Rest', dayType: 'rest' }
      ];
    } else if (presetKey === 'FBW_3_DAY') {
      newDaysList = [
        { dayLabel: lang === 'pl' ? 'Dzień 1 - FBW A (Push Focus)' : 'Day 1 - Full Body A (Push Focus)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 2 - Odpoczynek' : 'Day 2 - Rest', dayType: 'rest' },
        { dayLabel: lang === 'pl' ? 'Dzień 3 - FBW B (Pull Focus)' : 'Day 3 - Full Body B (Pull Focus)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 4 - Odpoczynek' : 'Day 4 - Rest', dayType: 'rest' },
        { dayLabel: lang === 'pl' ? 'Dzień 5 - FBW C (Legs Focus)' : 'Day 5 - Full Body C (Legs Focus)', dayType: 'workout' },
        { dayLabel: lang === 'pl' ? 'Dzień 6 - Odpoczynek' : 'Day 6 - Rest', dayType: 'rest' },
        { dayLabel: lang === 'pl' ? 'Dzień 7 - Odpoczynek' : 'Day 7 - Rest', dayType: 'rest' }
      ];
    }

    setDays(newDaysList.map((d, idx) => ({
      id: `preset_day_${Date.now()}_${idx}`,
      dayNumber: idx + 1,
      dayLabel: d.dayLabel,
      dayType: d.dayType,
      routineId: '',
      routineTitle: '',
      notes: '',
      showNotes: false
    })));
  };

  // Submit Handler
  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!title.trim()) {
      alert(lang === 'pl' ? "Wpisz nazwę cyklu treningowego." : "Please enter a plan title.");
      return;
    }

    const invalidDay = days.find(d => d.dayType === 'workout' && !d.routineId);
    if (invalidDay) {
      alert(lang === 'pl' ? `Przypisz szablon treningu do dnia: ${invalidDay.dayLabel}` : `Please assign a routine to: ${invalidDay.dayLabel}`);
      return;
    }

    setIsSaving(true);
    try {
      let assignmentModeVal = 'ALL_TRAINEES';
      let targetTraineeObj = null;

      if (isGlobalOnly || assignmentSelection === 'TEMPLATE_ONLY') {
        assignmentModeVal = 'TEMPLATE_ONLY';
      } else if (assignmentSelection === 'ALL_TRAINEES') {
        assignmentModeVal = 'ALL_TRAINEES';
      } else if (assignmentSelection.startsWith('TRAINEE_')) {
        assignmentModeVal = 'SPECIFIC_TRAINEE';
        const tId = assignmentSelection.replace('TRAINEE_', '');
        targetTraineeObj = activeTrainees.find(t => t.id === tId) || null;
      }

      await onSave({
        title: toTitleCase(title.trim()),
        notes: notes.trim(),
        durationWeeks: parseInt(durationWeeks) || 12,
        includeDeload: !!includeDeload,
        assignmentMode: isGlobalOnly ? 'GLOBAL' : assignmentModeVal,
        assignedTrainee: targetTraineeObj,
        days: days.map(d => ({
          dayNumber: d.dayNumber,
          dayLabel: d.dayLabel,
          dayType: d.dayType,
          routineId: d.routineId,
          routineTitle: d.routineTitle,
          notes: d.notes
        }))
      });
    } catch (err) {
      console.error("Save plan error:", err);
      alert("Error saving plan: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-[#0B0F19] text-slate-100 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden p-4 lg:p-6 space-y-6">
      
      {/* 1. TOP SECTION — PLAN CONFIGURATION & UNIFIED METADATA */}
      <div className="bg-slate-900/90 backdrop-blur-md border border-slate-800 rounded-2xl p-5 space-y-5 shadow-xl">
        
        {/* Title Input */}
        <div className="grid grid-cols-1 gap-4 items-center">
          
          {/* Plan Name Input */}
          <div className="space-y-1.5">
            <label className="block text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5">
              <span className="material-symbols-outlined text-sm text-indigo-400">folder_special</span>
              <span>{lang === 'pl' ? 'Nazwa Cyklu Treningowego *' : 'Plan Title *'}</span>
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={lang === 'pl' ? "np. 12-Tygodniowy Cykl Hipertroficzny & Siłowy..." : "e.g. 12-Week Hypertrophy & Strength Split..."}
              autoFocus
              className="w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 text-white font-extrabold text-sm lg:text-base rounded-xl px-4 h-11 outline-none shadow-inner transition"
            />
          </div>
        </div>

        {/* Unified Single Assignment Combobox & Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          
          {/* Unified Assignment Combobox */}
          {!isGlobalOnly && (
            <div className="lg:col-span-5 space-y-1.5">
              <label className="block text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5">
                <span className="material-symbols-outlined text-sm text-emerald-400">group</span>
                <span>{lang === 'pl' ? 'Przypisanie i Dostępność:' : 'Assignment & Target:'}</span>
              </label>
              <select
                value={assignmentSelection}
                onChange={(e) => setAssignmentSelection(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 text-emerald-400 font-bold text-xs rounded-xl px-3 h-11 outline-none cursor-pointer shadow-inner"
              >
                <option value="ALL_TRAINEES">👥 {lang === 'pl' ? 'Wszyscy Moi Zawodnicy' : 'All Trainees'}</option>
                <option value="TEMPLATE_ONLY">📂 {lang === 'pl' ? 'Szablon w Bibliotece (Bez odbiorcy)' : 'Save as Global Template (No Trainee)'}</option>
                {activeTrainees && activeTrainees.length > 0 && (
                  <optgroup label={lang === 'pl' ? 'Dedykowany Podopieczny:' : 'Specific Trainee:'}>
                    {activeTrainees.map(t => (
                      <option key={t.id} value={`TRAINEE_${t.id}`}>👤 {t.name || t.displayName || t.email}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          )}

          {/* Coaching Notes / Guidelines Textarea */}
          <div className={`${isGlobalOnly ? 'lg:col-span-12' : 'lg:col-span-7'} space-y-1.5`}>
            <label className="block text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5">
              <span className="material-symbols-outlined text-sm text-indigo-400">edit_note</span>
              <span>{lang === 'pl' ? 'Wytyczne i Wskazówki Trenera:' : 'Coaching Guidelines & Notes:'}</span>
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={lang === 'pl' ? "Wpisz zalecenia dotyczące regeneracji, suplementacji, makro czy progresji..." : "Write instructions regarding recovery, nutrition, or progression rules..."}
              className="w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 text-slate-200 font-medium text-xs rounded-xl p-3 outline-none resize-none shadow-inner leading-relaxed"
            />
          </div>
        </div>

        {/* Weekly Telemetry Summary Bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-slate-800/80 text-xs font-semibold">
          <div className="flex items-center gap-2 flex-wrap text-slate-300">
            
            {/* Workout Days vs Rest Days Badge */}
            <div className="flex items-center gap-1.5 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-lg">
              <span className="material-symbols-outlined text-sm text-indigo-400">fitness_center</span>
              <span>{telemetry.workoutDaysCount} {lang === 'pl' ? 'Treningi' : 'Workouts'} / {telemetry.restDaysCount} {lang === 'pl' ? 'Regeneracja' : 'Rest'}</span>
            </div>

            {/* Total Weekly Sets Badge */}
            <div className="flex items-center gap-1.5 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-lg">
              <span className="material-symbols-outlined text-sm text-cyan-400">bolt</span>
              <span>{telemetry.totalWeeklySets} {lang === 'pl' ? 'Serii Tygodniowo' : 'Weekly Sets'}</span>
            </div>

            {/* Estimated Weekly Duration Badge */}
            <div className="flex items-center gap-1.5 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-lg">
              <span className="material-symbols-outlined text-sm text-slate-400">schedule</span>
              <span>~{telemetry.totalWeeklyDurationMin} min {lang === 'pl' ? '/ tydzień' : '/ week'}</span>
            </div>
          </div>

          <div className="text-slate-400 text-xs font-bold ml-auto">
            {lang === 'pl' ? `Łączny wolumen: ~${telemetry.totalWeeklySets} serii / tydzień` : `Total weekly volume: ~${telemetry.totalWeeklySets} sets`}
          </div>
        </div>
      </div>

      {/* 2. SINGLE INTUITIVE SCHEDULE LIST (CLEAN HEADER) */}
      <div className="space-y-4">
        
        {/* Schedule Header (Clean, No Mode Toggle Buttons) */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-xl text-indigo-400">calendar_month</span>
            <h3 className="text-base font-extrabold text-white m-0 tracking-tight">
              {lang === 'pl' ? 'Harmonogram Dni Treningowych' : 'Training Days & Schedule'}
            </h3>
          </div>
          <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">
            {days.length} {lang === 'pl' ? 'Dni w Cyklu' : 'Days in Split'}
          </span>
        </div>

        {/* Days Cards Container with Native Drag-and-Drop */}
        <div className="space-y-3.5">
          {days.map((day, idx) => {
            const isRestDay = day.dayType === 'rest';

            return (
              <div
                key={day.id || idx}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                className={`transition-all ${
                  draggedIdx === idx ? 'opacity-40 scale-[0.99] border-indigo-500' : ''
                } ${
                  isRestDay
                    ? 'border-2 border-dashed border-slate-800 bg-slate-950/60 opacity-85 rounded-2xl p-4 space-y-3'
                    : 'bg-slate-900/90 border border-slate-800 rounded-2xl p-4.5 space-y-3.5 shadow-xl hover:border-slate-700'
                }`}
              >
                {/* Day Header Row (Unified Flex Alignment) */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  
                  {/* Left: Drag Handle, Badge & Editable Title */}
                  <div className="flex items-center gap-2.5 flex-1 min-w-[280px]">
                    
                    {/* Drag Handle (⋮⋮) */}
                    <span 
                      className="material-symbols-outlined text-lg text-slate-500 hover:text-indigo-400 cursor-grab active:cursor-grabbing select-none" 
                      title={lang === 'pl' ? "Przeciągnij, aby zmienić kolejność dni" : "Drag handle to reorder days"}
                    >
                      drag_indicator
                    </span>
                    
                    {/* Day Number Badge */}
                    <span className={`w-7 h-7 rounded-lg font-bold flex items-center justify-center text-xs shrink-0 border ${
                      isRestDay ? 'bg-slate-950 text-slate-400 border-slate-800' : 'bg-indigo-600 text-white border-indigo-400 shadow-sm'
                    }`}>
                      {day.dayNumber}
                    </span>

                    {/* Day Title / Label Input */}
                    <input
                      type="text"
                      required
                      value={day.dayLabel}
                      onChange={(e) => handleUpdateDayField(idx, 'dayLabel', e.target.value)}
                      placeholder={lang === 'pl' ? "Nazwa Dnia (np. Push - Klatka/Barki)..." : "Day Label (e.g. Push - Chest Focus)..."}
                      className="bg-slate-950 border border-slate-800 focus:border-indigo-500 text-white font-extrabold text-xs lg:text-sm rounded-xl px-3 h-10 outline-none flex-1 shadow-inner"
                    />
                  </div>

                  {/* Right: Day Type Toggle, Peek Preview, Duplicate, Notes & Delete */}
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    
                    {/* Day Type Toggle Button */}
                    <button
                      type="button"
                      onClick={() => handleToggleDayType(idx)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer border flex items-center gap-1.5 ${
                        isRestDay 
                          ? 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white' 
                          : 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30'
                      }`}
                    >
                      <span className="material-symbols-outlined text-xs">{isRestDay ? 'nightlight' : 'fitness_center'}</span>
                      <span>{isRestDay ? (lang === 'pl' ? 'Odpoczynek' : 'Rest') : (lang === 'pl' ? 'Dzień Treningowy' : 'Workout')}</span>
                    </button>

                    {/* Inline Peek Preview Button (Only for Workout Days) */}
                    {!isRestDay && (
                      <button
                        type="button"
                        disabled={!day.routineId}
                        onClick={() => {
                          const rt = routinesList.find(r => r.id === day.routineId);
                          if (rt) setPreviewRoutine(rt);
                        }}
                        className="bg-slate-950 hover:bg-slate-800 text-indigo-300 hover:text-white disabled:opacity-30 border border-slate-800 text-xs font-bold px-2.5 py-1.5 rounded-lg transition cursor-pointer flex items-center gap-1"
                        title={lang === 'pl' ? "Szybki podgląd ćwiczeń" : "Peek exercise breakdown"}
                      >
                        <span className="material-symbols-outlined text-xs">visibility</span>
                        <span>{lang === 'pl' ? 'Podgląd' : 'Peek'}</span>
                      </button>
                    )}

                    {/* Toggle Day Notes Button */}
                    <button
                      type="button"
                      onClick={() => handleUpdateDayField(idx, 'showNotes', !day.showNotes)}
                      className={`p-1.5 rounded-lg text-xs font-bold border transition cursor-pointer ${
                        day.showNotes ? 'bg-indigo-600 text-white border-indigo-400' : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                      }`}
                      title={lang === 'pl' ? "Dodaj notatkę do dnia" : "Add notes for this day"}
                    >
                      <span className="material-symbols-outlined text-xs">edit_note</span>
                    </button>

                    {/* Duplicate Day Button */}
                    <button
                      type="button"
                      onClick={() => handleDuplicateDay(idx)}
                      className="bg-slate-950 hover:bg-slate-800 text-slate-300 p-1.5 rounded-lg text-xs font-bold border border-slate-800 transition cursor-pointer"
                      title={lang === 'pl' ? "Duplikuj ten dzień" : "Duplicate day"}
                    >
                      <span className="material-symbols-outlined text-xs">content_copy</span>
                    </button>

                    {/* Remove Day Button */}
                    {days.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveDay(idx)}
                        className="bg-slate-950 hover:bg-rose-950/60 text-slate-400 hover:text-rose-400 p-1.5 rounded-lg transition border border-slate-800 cursor-pointer"
                        title={lang === 'pl' ? "Usuń ten dzień" : "Remove day"}
                      >
                        <span className="material-symbols-outlined text-xs">delete</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Workout Routine Selector (Only for Workout Days) */}
                {!isRestDay && (
                  <div className="pt-1">
                    <select
                      required
                      value={day.routineId}
                      onChange={(e) => handleUpdateDayField(idx, 'routineId', e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 text-indigo-300 font-extrabold text-xs rounded-xl px-3 h-10 outline-none focus:border-indigo-500 cursor-pointer shadow-inner"
                    >
                      <option value="">-- {lang === 'pl' ? 'Wybierz szablon treningu z biblioteki' : 'Select routine template from library'} --</option>
                      {routinesList.map(r => {
                        const rtKey = r.id || r.docId || r.routineId;
                        const exCount = (r.exercises || r.exerciseList || r.routineExercises || []).length || parseInt(r.setsCount) || 0;
                        return (
                          <option key={rtKey} value={rtKey}>
                            {r.title || r.name} ({r.tag || '#FBW'}) — {exCount} {lang === 'pl' ? 'ćwiczeń' : 'exercises'}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Day Notes Textarea */}
                {day.showNotes && (
                  <div className="pt-1">
                    <textarea
                      rows={2}
                      value={day.notes || ''}
                      onChange={(e) => handleUpdateDayField(idx, 'notes', e.target.value)}
                      placeholder={lang === 'pl' ? "Wpisz dodatkowe uwagi dla tego dnia (np. kardio po treningu, rozciąganie)..." : "Add specific day instructions (e.g., post-workout cardio)..."}
                      className="w-full bg-slate-950 border border-slate-800 text-slate-200 text-xs rounded-xl p-2.5 outline-none resize-none shadow-inner"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. QUICK ACTION TOOLBAR */}
      <div className="sticky bottom-0 bg-slate-950/95 backdrop-blur-md border border-slate-800 p-4 rounded-2xl flex items-center justify-between flex-wrap gap-3 shadow-2xl">
        
        {/* Quick Add Days & Presets */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => handleAddDay('workout')}
            className="bg-indigo-600/20 hover:bg-indigo-600 text-indigo-300 hover:text-white border border-indigo-500/30 text-xs font-bold px-3.5 py-2 rounded-xl transition cursor-pointer flex items-center gap-1.5"
          >
            <span className="material-symbols-outlined text-xs">add_circle</span>
            <span>+ {lang === 'pl' ? 'Dodaj Dzień Treningowy' : 'Add Workout Day'}</span>
          </button>

          <button
            type="button"
            onClick={() => handleAddDay('rest')}
            className="bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 text-xs font-bold px-3.5 py-2 rounded-xl transition cursor-pointer flex items-center gap-1.5"
          >
            <span className="material-symbols-outlined text-xs">nightlight</span>
            <span>+ {lang === 'pl' ? 'Dodaj Dzień Odpoczynku' : 'Add Rest Day'}</span>
          </button>

          {/* Preset Splits Select Dropdown (Fully Localized) */}
          <select
            onChange={(e) => {
              if (e.target.value) handleApplyPresetSplit(e.target.value);
              e.target.value = "";
            }}
            className="bg-slate-900 border border-slate-800 text-slate-300 font-bold text-xs rounded-xl px-3 py-2 outline-none cursor-pointer"
          >
            <option value="">⚡ {lang === 'pl' ? 'Wczytaj Gotowy Szablon Dni...' : 'Apply Preset Split...'}</option>
            <option value="PPL_6_DAY">⚡ {lang === 'pl' ? 'Push / Pull / Legs (6 Dni)' : 'Push / Pull / Legs 6-Day'}</option>
            <option value="UPPER_LOWER_4_DAY">⚡ {lang === 'pl' ? 'Upper / Lower (4 Dni)' : 'Upper / Lower 4-Day'}</option>
            <option value="FBW_3_DAY">⚡ {lang === 'pl' ? 'Full Body Workout (3 Dni)' : 'Full Body 3-Day'}</option>
          </select>
        </div>

        {/* Main CTA: Save & Deploy Training Plan */}
        <div className="flex items-center gap-3 ml-auto">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-400 hover:text-white transition cursor-pointer"
            >
              {lang === 'pl' ? 'Anuluj' : 'Cancel'}
            </button>
          )}

          <button
            type="button"
            disabled={isSaving || !title.trim()}
            onClick={handleFormSubmit}
            className="bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white font-extrabold text-xs px-7 py-3 rounded-xl shadow-lg shadow-emerald-950/40 disabled:opacity-40 cursor-pointer transition flex items-center gap-2 border border-emerald-500/30"
          >
            <span className="material-symbols-outlined text-base">rocket_launch</span>
            <span>{isSaving ? (lang === 'pl' ? 'Zapisywanie...' : 'Saving...') : (lang === 'pl' ? 'Zapisz i Wdróż Cykl Treningowy' : 'Save & Deploy Training Plan')}</span>
          </button>
        </div>
      </div>

      {/* ROUTINE PEEK PREVIEW MODAL */}
      {previewRoutine && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewRoutine(null)}
        >
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-lg w-full space-y-4 relative shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">{previewRoutine.tag || '#FBW'}</span>
                <h3 className="text-base font-extrabold text-white m-0">{previewRoutine.title}</h3>
              </div>
              <button 
                type="button" 
                onClick={() => setPreviewRoutine(null)}
                className="text-slate-400 hover:text-white font-bold text-sm"
              >
                ✕
              </button>
            </div>

            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
              {(previewRoutine.exercises || []).map((ex, idx) => (
                <div key={idx} className="bg-slate-950 border border-slate-800 p-3 rounded-xl space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-extrabold text-white">{idx + 1}. {toTitleCase(ex.namePL || ex.name)}</span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">{ex.setsList?.length || ex.sets || 3} {lang === 'pl' ? 'serii' : 'sets'}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-medium">
                    {ex.bodyPart || ex.category} • {ex.equipment}
                  </div>
                  {ex.notes && (
                    <p className="text-[11px] text-slate-400 italic m-0 pt-1 border-t border-slate-900">{ex.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
